import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Mail } from "lucide-react";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SUPPORT_EMAIL } from "../lib/legalContent";

const TOPICS = [["club", "Club enquiry"], ["account", "My account or club payments"], ["tech", "Technical problem"]];
const field = "bg-black/40 border border-white/10 rounded-lg px-3 py-3 text-base text-slate-100 focus:border-amber-400 outline-none";

export default function ContactPage() {
  const { user } = useAuth();
  const [f, setF] = useState({ name: user?.display_name || "", email: user?.email || "", topic: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ref, setRef] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.topic) return setErr("Choose what your message is about");
    setBusy(true); setErr("");
    try { setRef((await api.post("/api/contact", f)).data.ref || "SENT"); } catch (ex) { setErr(errorMessage(ex)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen px-5 sm:px-6 py-8 max-w-xl mx-auto flex flex-col gap-6 noise" data-testid="contact-page">
      <Link to={user ? "/lobby" : "/"} className="btn-gold rounded-full pl-2 pr-3 py-1.5 text-xs font-display tracking-wider flex items-center gap-1.5 self-start btn-lift" data-testid="contact-back">
        <ArrowLeft className="w-4 h-4" /> {user ? "Lobby" : "Back"}
      </Link>
      <header className="flex items-center gap-3">
        <Mail className="w-8 h-8 gold-text shrink-0" />
        <div>
          <h1 className="font-display text-2xl sm:text-3xl text-amber-50">Contact us</h1>
          <p className="text-sm text-slate-400">Pick a topic and we'll make sure the right person reads it.</p>
        </div>
      </header>
      {ref ? (
        <div className="rounded-2xl border border-emerald-400/30 bg-emerald-400/5 px-4 py-4 text-sm text-emerald-100 flex gap-3" data-testid="contact-sent">
          <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-300" />
          <div>Thanks, your message is on its way. Your reference is <strong data-testid="contact-ref">{ref}</strong>. We've emailed you a copy, and we'll reply to {f.email}.</div>
        </div>
      ) : (
        <form onSubmit={submit} className="glass rounded-3xl p-6 flex flex-col gap-4" data-testid="contact-form">
          <div className="flex flex-col gap-2">
            <span className="text-xs text-slate-400">What is it about?</span>
            <div className="flex flex-wrap gap-2">
              {TOPICS.map(([k, label]) => (
                <button type="button" key={k} data-testid={`contact-topic-${k}`} onClick={() => setF({ ...f, topic: k })}
                  className={`rounded-full px-4 py-2 text-sm border transition-colors ${f.topic === k ? "bg-amber-500 text-slate-900 border-amber-500" : "border-white/15 text-slate-200 hover:border-amber-400/60"}`}>{label}</button>
              ))}
            </div>
          </div>
          <label className="flex flex-col gap-1 text-xs text-slate-400">Name
            <input data-testid="contact-name" required minLength={2} maxLength={60} value={f.name} onChange={set("name")} className={field} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">Email
            <input data-testid="contact-email" type="email" required value={f.email} onChange={set("email")} className={field} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">Message
            <textarea data-testid="contact-message" required minLength={10} maxLength={3000} rows={6} value={f.message} onChange={set("message")} className={`${field} resize-y`} />
          </label>
          <input tabIndex={-1} autoComplete="off" aria-hidden value={f.website} onChange={set("website")} className="hidden" name="website" />
          {err && <div data-testid="contact-error" className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{err}</div>}
          <button data-testid="contact-submit" disabled={busy} className="rounded-full py-3 font-display text-sm bg-amber-500 hover:bg-amber-400 text-slate-900 btn-lift disabled:opacity-50">{busy ? "…" : "SEND MESSAGE"}</button>
        </form>
      )}
      <p className="text-xs text-slate-500">Prefer email? Technical help: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-amber-300 hover:text-amber-200" data-testid="contact-support-email">{SUPPORT_EMAIL}</a></p>
    </div>
  );
}
